import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, X, Lightbulb, BookOpen, Download, 
  Play, Users, Star, Clock, Target, ChevronRight,
  CheckCircle, AlertCircle, Info
} from 'lucide-react';
import { UserJourneyStage, AvailableAction, EngagementMetrics } from '../../types/enhancedAnalysis';

interface ProgressiveActionGuideProps {
  currentStage: UserJourneyStage;
  readingProgress: number;
  engagementMetrics: EngagementMetrics;
  availableActions: AvailableAction[];
  onActionSelect: (action: AvailableAction) => void;
  onDismiss: () => void;
}

const ProgressiveActionGuide: React.FC<ProgressiveActionGuideProps> = ({
  currentStage,
  readingProgress,
  engagementMetrics,
  availableActions,
  onActionSelect,
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentActionIndex, setCurrentActionIndex] = useState(0);
  const [dismissedActions, setDismissedActions] = useState<Set<string>>(new Set());

  // 根据用户行为决定是否显示引导
  useEffect(() => {
    const shouldShow = determineVisibility();
    setIsVisible(shouldShow);
  }, [currentStage, readingProgress, engagementMetrics]);

  const determineVisibility = (): boolean => {
    // 如果用户已经完成阅读且参与度高，显示学习引导
    if (readingProgress > 80 && engagementMetrics.timeSpent > 300) {
      return true;
    }
    
    // 如果用户在某个章节停留时间较长，显示相关操作
    if (engagementMetrics.timeSpent > 180 && readingProgress > 50) {
      return true;
    }
    
    // 如果用户表现出学习意图，显示实践引导
    if (engagementMetrics.methodologyViewed && !engagementMetrics.practiceStarted) {
      return true;
    }
    
    return false;
  };

  const getStageIcon = (stage: UserJourneyStage) => {
    switch (stage) {
      case 'reading': return <BookOpen className="w-5 h-5" />;
      case 'engaged': return <Star className="w-5 h-5" />;
      case 'learning': return <Lightbulb className="w-5 h-5" />;
      case 'practicing': return <Play className="w-5 h-5" />;
      case 'completed': return <CheckCircle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getStageMessage = (stage: UserJourneyStage): string => {
    switch (stage) {
      case 'reading':
        return '您正在深度阅读这篇分析，很棒！';
      case 'engaged':
        return '看起来您对这个分析很感兴趣！';
      case 'learning':
        return '准备好学习分析方法了吗？';
      case 'practicing':
        return '动手实践是最好的学习方式！';
      case 'completed':
        return '恭喜完成阅读！继续探索更多内容吧。';
      default:
        return '发现更多深度内容';
    }
  };

  const getActionIcon = (action: AvailableAction) => {
    switch (action.type) {
      case 'primary': return <Target className="w-4 h-4" />;
      case 'secondary': return <ArrowRight className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getActionPriority = (action: AvailableAction): number => {
    // 根据用户当前状态和行为计算操作优先级
    let priority = 0;
    
    if (action.type === 'primary') priority += 3;
    else if (action.type === 'secondary') priority += 2;
    else priority += 1;
    
    // 根据用户参与度调整优先级
    if (engagementMetrics.timeSpent > 300) priority += 2;
    if (engagementMetrics.methodologyViewed) priority += 1;
    if (readingProgress > 80) priority += 2;
    
    return priority;
  };

  const filteredActions = availableActions
    .filter(action => !dismissedActions.has(action.id))
    .sort((a, b) => getActionPriority(b) - getActionPriority(a))
    .slice(0, 3); // 最多显示3个操作

  const handleActionClick = (action: AvailableAction) => {
    onActionSelect(action);
    setDismissedActions(prev => new Set([...prev, action.id]));
  };

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss();
  };

  if (!isVisible || filteredActions.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 max-w-sm z-50 animate-slide-up">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* 头部 */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                {getStageIcon(currentStage)}
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  智能推荐
                </div>
                <div className="text-xs text-gray-600">
                  基于您的阅读行为
                </div>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 内容 */}
        <div className="p-4">
          {/* 阶段消息 */}
          <div className="mb-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              {getStageMessage(currentStage)}
            </p>
            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
              <span>阅读进度: {Math.round(readingProgress)}%</span>
              <span>停留时间: {Math.round(engagementMetrics.timeSpent / 60)}分钟</span>
            </div>
          </div>

          {/* 推荐操作 */}
          <div className="space-y-3">
            {filteredActions.map((action, index) => (
              <div
                key={action.id}
                className={`relative ${index === 0 ? 'ring-2 ring-blue-200 ring-opacity-50' : ''}`}
              >
                <button
                  onClick={() => handleActionClick(action)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      action.type === 'primary' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {getActionIcon(action)}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900">
                        {action.title}
                      </div>
                      <div className="text-xs text-gray-600">
                        {action.description}
                      </div>
                      {action.estimatedTime > 0 && (
                        <div className="flex items-center space-x-1 mt-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            约 {action.estimatedTime} 分钟
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </button>
                
                {/* 优先级指示器 */}
                {index === 0 && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    !
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 底部提示 */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>个性化推荐</span>
              <button
                onClick={handleDismiss}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                稍后提醒
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 动画样式
const styles = `
@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  animation: slide-up 0.3s ease-out;
}
`;

// 将样式注入到页面
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default ProgressiveActionGuide;